import {
  GenericForm,
  GenericFormLogicalField,
  genericBeforeValidate_removeDefaultKeyedError,
  genericValidate_textField,
  GenericFormValidationContext,
  ValidationEvent,
  GenericTextFieldValidationOptions,
} from "../../../hooks";

import { replaceAll } from "../../../utils";

const maxMobileNumberLength = 12;

const getCurrentCountryCode = () => {
  try {
    const currentCountryNode = document.querySelector(
      '[data-block="country-selector"] [data-js="current-country"]'
    );
    if (currentCountryNode instanceof HTMLElement) {
      const countryCode = currentCountryNode.dataset?.countryCode;
      if (countryCode) {
        return countryCode.toLowerCase();
      }
    }
  } catch (error) {}

  try {
    const checkoutConfigStoreCode = (window as any)?.checkoutConfig?.storeCode;
    const storeCode = String(checkoutConfigStoreCode || "").toLowerCase();
    if (storeCode.includes("nz")) {
      return "nz";
    }
    if (storeCode.includes("sg")) {
      return "sg";
    }
  } catch (error) {}

  return "au";
};

const isValidAustralianMobileNumber = (value: string) =>
  /^(?:04\d{8}|05\d{8}|\+614\d{8}|\+615\d{8}|614\d{8}|615\d{8})$/.test(
    value || ""
  );

const normalizeCountryCode = (value: unknown) => {
  const normalizedValue = String(value || "").trim().toLowerCase();
  return normalizedValue.length > 0 ? normalizedValue : null;
};

const getManualCountryCodeFromForm = <Form extends GenericForm, App extends unknown>(
  context: GenericFormValidationContext<Form, App>
) => {
  if (!context.name.endsWith("_mobile")) {
    return null;
  }

  const manualCountryFieldName = context.name.replace(
    /_mobile$/,
    "_manualAddressCountry"
  );
  const formState = context.form as Record<string, unknown>;

  return normalizeCountryCode(formState?.[manualCountryFieldName]);
};

const getCountryCodeForMobileValidation = <
  Form extends GenericForm,
  App extends unknown
>(
  context: GenericFormValidationContext<Form, App>
) => {
  const manualCountryCode = getManualCountryCodeFromForm(context);
  if (manualCountryCode) {
    return manualCountryCode;
  }

  const appCountryCode = normalizeCountryCode((context.app as any)?.countryCode);
  if (appCountryCode) {
    return appCountryCode;
  }

  return getCurrentCountryCode();
};

interface MobileFormLogicProps<Form extends GenericForm, App extends unknown> {
  required?: boolean;
  guard?: GenericTextFieldValidationOptions<Form, App>["guard"];
}

export const mobileFormLogic = <Form extends GenericForm, App extends unknown>(
  props: MobileFormLogicProps<Form, App> = {}
): GenericFormLogicalField<Form, App> => ({
  presentation: props,
  validationEvents: [
    ValidationEvent.onChange,
    ValidationEvent.onBlur,
    ValidationEvent.onSubmit,
  ],
  beforeValidate: genericBeforeValidate_removeDefaultKeyedError,
  validate: genericValidate_textField({
    guard: props.guard,
    required: props.required,
    customLength: (value: any) => {
      const valueWithoutPlus = replaceAll(value || "", "\\+", "");
      let newValue = valueWithoutPlus.substring(0, maxMobileNumberLength);
      if (((value as string) || "").includes("+")) {
        newValue = `+${newValue}`;
      }
      return {
        value: newValue,
      };
    },
    customErrorValidation: (value: any, _options, context) => {
      const mobileValue = String(value || "").trim();
      if (!props.required && !mobileValue) {
        return null;
      }

      const currentCountryCode = getCountryCodeForMobileValidation(context);
      if (currentCountryCode === "au") {
        if (!isValidAustralianMobileNumber(mobileValue)) {
          return "Please enter a valid mobile number.";
        }

        return null;
      }

      const valueWithoutPlus = replaceAll(value || "", "\\+", "");
      const isAnythingOtherThanDigits = /[^\d]/.test(valueWithoutPlus);
      if (isAnythingOtherThanDigits) {
        return "Please enter a valid number in this field.";
      }
      return null;
    },
  }),
});
